import { Spinner, Row, Col, Card } from "react-bootstrap";
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { COLOR, STYLE } from '../../../../config/constraits';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef } from "react";
import { generateCsvFile } from "../../../../utils/downloadCsvFile";
import { ClaimServiceInidicatorPieChart } from "../../types";
import { saveBeforeFilter, addFilter } from "../../../../redux/filtersSlice";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ChartShareActions from "../../../../components/ChartShareActions";
import { RootState } from "../../../../redux/store";
import { useClaimStatus } from "../../hooks/useClaimStatus";
import { AuditActionEnum } from "../../../auditLogs/types";

interface ClaimsStatusPieChartProps {
    setMessage: (msg: string) => void;
}
export default function ClaimsStatusPieChart({ setMessage }: ClaimsStatusPieChartProps) {
    const { t } = useTranslation('claim');
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { data, isLoading, error } = useClaimStatus();

    useEffect(() => {
        if (error) {
            const messageError = (error as any)?.response?.data?.message || (error as Error).message;
            setMessage(messageError);
        }
    }, [error, setMessage]);

    const { openStatusPercentage, inProccesStatusPercentage, closedStatusPercentage, openCount, inProcessCount, closedCount} = useMemo(() => {
        if (!data) {
            return {
                openCount: 0,
                inProcessCount: 0,
                closedCount: 0,
                openStatusPercentage: 0,
                inProccesStatusPercentage: 0,
                closedStatusPercentage: 0
            };
        }

        const countOpen = data.items.find((item) => item.status === 0)?.count ?? 0;
        const countInProcess = data.items.find((item) => item.status === 1)?.count ?? 0;
        const countClosed = data.items.find((item) => item.status === 2)?.count ?? 0;
        const safeTotal = data.total > 0 ? data.total : 1;

        return {
            openCount: countOpen,
            inProcessCount: countInProcess,
            closedCount: countClosed,
            openStatusPercentage: (countOpen * 100) / safeTotal,
            inProccesStatusPercentage: (countInProcess * 100) / safeTotal,
            closedStatusPercentage: (countClosed * 100) / safeTotal
        };
    }, [data]);

    const isEmptyData = openStatusPercentage + inProccesStatusPercentage + closedStatusPercentage === 0;

    const ChartData = useMemo(() =>
        isEmptyData
            ? [
                { name: t("claim:statusPieChart.labelOpen"), value: 100, fixedValue: '0', color: '#BFBFBF', valueInt: "0" },
                { name: t("claim:statusPieChart.labelInProcess"), value: 100, fixedValue: '0', color: '#BFBFBF', valueInt: "1" },
                { name: t("claim:statusPieChart.labelClosed"), value: 100, fixedValue: '0', color: '#BFBFBF', valueInt: "2" },
            ]
            : [
                { name: t("claim:statusPieChart.labelOpen"), value: openStatusPercentage, fixedValue: openStatusPercentage.toFixed(), color: COLOR.PURPLE, valueInt: "0" },
                { name: t("claim:statusPieChart.labelInProcess"), value: inProccesStatusPercentage, fixedValue: inProccesStatusPercentage.toFixed(), color: COLOR.TARONJA, valueInt: "1" },
                { name: t("claim:statusPieChart.labelClosed"), value: closedStatusPercentage, fixedValue: closedStatusPercentage.toFixed(), color: COLOR.VERD, valueInt: "2" },
            ],
    [isEmptyData, openStatusPercentage, inProccesStatusPercentage, closedStatusPercentage, t]);

    const handleSegmentClick = (e: string) => {
        const filter = {
            name: 'Status',
            values: [e]
        };
        dispatch(saveBeforeFilter('claims'));
        dispatch(addFilter({ table: 'claims', filter: filter }));
        navigate('/claims/historic');
    };

    const feelingchartRef = useRef<HTMLDivElement>(null);
    const selectedFilters = useSelector((state: RootState) => state.filters.claims.selectedFilters);

    const exportPieChartCsvDataDto: ClaimServiceInidicatorPieChart = {
        countOpen: openCount,
        countClosed: closedCount,
        countProgress: inProcessCount,
        openPercentage: openStatusPercentage,
        closedPercentage: closedStatusPercentage,
        progressPercentage: inProccesStatusPercentage,
    };

    const exportPieChartCsvData = async (): Promise<void> => {
        const rows = [
            {
                state: t("claim:statusPieChart.labelOpen"),
                claims: exportPieChartCsvDataDto.countOpen ?? 0,
                percentage: exportPieChartCsvDataDto.openPercentage ?? 0,
            },
            {
                state: t("claim:statusPieChart.labelInProcess"),
                claims: exportPieChartCsvDataDto.countProgress ?? 0,
                percentage: exportPieChartCsvDataDto.progressPercentage ?? 0,
            },
            {
                state: t("claim:statusPieChart.labelClosed"),
                claims: exportPieChartCsvDataDto.countClosed ?? 0,
                percentage: exportPieChartCsvDataDto.closedPercentage ?? 0,
            },
        ];

        generateCsvFile({
            headers: [
                t("claim:statusPieChart.export.headerState"),
                t("claim:statusPieChart.export.headerClaims"),
                t("claim:statusPieChart.export.headerPercentage"),
            ],
            rows,
            mapRow: (item) => [item.state, item.claims, Number(item.percentage.toFixed(2))],
            fileBaseName: "claims_status_distribution",
            trailingSemicolon: true,
        });
    };


    return (
        <>
            {isLoading && (
                <div className="overlay-spinner">
                    <Spinner animation="border" variant="light" />
                </div>
            )}
            <div ref={feelingchartRef}>
                <Card
                    style={{
                        backgroundColor: "#F5F5F5",
                        border: "none",
                        borderRadius: STYLE.BORDER_RADIUS,
                    }}
                    className="p-3 d-flex justify-content-center align-items-center text-center"
                >
                    <div className="d-flex justify-content-between align-items-center mb-3 w-100">
                        <h4 className="mb-0">{t("claim:statusPieChart.labelTitle")}</h4>
                        <ChartShareActions
                            chartRef={feelingchartRef}
                            title={t("claim:statusPieChart.labelTitle")}
                            filters={selectedFilters}
                            t={t}
                            exportCsvAction={exportPieChartCsvData}
                            exportCsvAudit={{
                                entity: "DashboardIndicator",
                                action: AuditActionEnum.ExportCsv,
                                source: window.location.pathname,
                                description: "CSV export of claim status pie chart",
                                filters: selectedFilters
                            }}
                        />
                    </div>
                    <ResponsiveContainer width="100%" height={280}>
                        <PieChart width={200} height={200}>
                            <Pie
                                data={ChartData}
                                dataKey="value"
                                nameKey="name"
                                innerRadius={84}
                                outerRadius={120}
                                startAngle={90}
                                endAngle={-270}
                                fill="#82ca9d"
                            >
                                {ChartData.map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={entry.color}
                                        style={{ cursor: "pointer" }}
                                        onClick={() => handleSegmentClick(entry.valueInt)}
                                    />
                                ))}
                            </Pie>
                        </PieChart>
                    </ResponsiveContainer>
                    <Row>
                        <Col className="d-flex justify-content-center">
                            <div>
                                {ChartData.map((item, index) => (
                                    <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                                        <span
                                            className="rounded-circle"
                                            style={{
                                                width: '10px',
                                                height: '10px',
                                                display: 'inline-block',
                                                marginRight: '8px',
                                                backgroundColor: item.color
                                            }}
                                        />
                                        <p style={{ margin: 0 }}>
                                            {item.name}: {isNaN(Number(item.fixedValue)) ? '0' : item.fixedValue}%
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </Col>
                    </Row>
                </Card>
            </div>
        </>
    );
}
